// src/asset-delivery/api.ts

import baseApi from "../lib/baseApi";
import type { PlaybackTarget } from "./orchestrator/types";
import type { ResolvedPlayback } from "./orchestrator/types";

type PlaybackURLResponse = {
  url: string;
  expires_in?: number;   // backend primary
  expires_at?: string;   // backend convenience
};

function buildParams(target: PlaybackTarget) {
  return { ...target };
}

async function fetchPlaybackURL(
  path: string,
  target: PlaybackTarget,
  fieldName: string
): Promise<ResolvedPlayback> {
  const resp = await baseApi.get<PlaybackURLResponse>(path, {
    params: { ...buildParams(target), field_name: fieldName, intent: "preload" },
  });

  return {
    url: resp.data.url,
    expiresAt: resp.data.expires_at,
  };
}

export function fetchVideoPlaybackURL(target: PlaybackTarget, fieldName = "video") {
  return fetchPlaybackURL("/assets/playback/video/", target, fieldName);
}

export function fetchAudioPlaybackURL(target: PlaybackTarget, fieldName = "audio") {
  return fetchPlaybackURL("/assets/playback/audio/", target, fieldName);
}

export function fetchImagePlaybackURL(target: PlaybackTarget, fieldName = "image") {
  return fetchPlaybackURL("/assets/playback/image/", target, fieldName);
}

export function fetchThumbnailPlaybackURL(target: PlaybackTarget, fieldName = "thumbnail") {
  return fetchPlaybackURL("/assets/playback/thumbnail/", target, fieldName);
}

/**
 * File playback:
 * - Preferred: /assets/playback/file/
 * - Fallback: /assets/playback/image/ with field_name=file (non-breaking)
 */
export async function fetchFilePlaybackURL(target: PlaybackTarget, fieldName = "file") {
  try {
    return await fetchPlaybackURL("/assets/playback/file/", target, fieldName);
  } catch (err: any) {
    // Safe fallback for older backends (avoid breaking)
    return await fetchPlaybackURL("/assets/playback/image/", target, fieldName);
  }
}
